"use client";

import React, { useCallback, useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { isConsentGiven } from "../layout/footer/cookieConsent";

const STORAGE_KEY = "mcl_whatsapp_discount_popup";
const SHOW_AGAIN_AFTER_DAYS = 7;
const SHOW_DELAY_MS = 6000;

const DISCOUNT_CODE = "MINI20";
const PHONE_NUMBER = "31685388031";

function wasRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { dismissedAt } = JSON.parse(raw);
    if (!dismissedAt) return false;
    const ageMs = Date.now() - dismissedAt;
    return ageMs < SHOW_AGAIN_AFTER_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ dismissedAt: Date.now() }));
  } catch {
    /* storage unavailable */
  }
}

function WhatsAppDiscountPopup() {
  const t = useTranslations("discountPopup");
  const locale = useLocale();

  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (wasRecentlyDismissed()) return;

    let showTimeout: ReturnType<typeof setTimeout> | null = null;

    // Wait until the visitor has accepted cookies, then start the delay timer.
    const consentInterval = setInterval(() => {
      if (isConsentGiven()) {
        clearInterval(consentInterval);
        showTimeout = setTimeout(() => {
          if (!wasRecentlyDismissed()) setVisible(true);
        }, SHOW_DELAY_MS);
      }
    }, 1000);

    return () => {
      clearInterval(consentInterval);
      if (showTimeout) clearTimeout(showTimeout);
    };
  }, []);

  const close = useCallback(() => {
    setVisible(false);
    markDismissed();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [visible, close]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(DISCOUNT_CODE);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleWhatsAppClick = () => {
    if (typeof window !== "undefined" && Array.isArray((window as any).dataLayer)) {
      (window as any).dataLayer.push({
        event: "discount_popup_whatsapp_click",
        discount_code: DISCOUNT_CODE,
      });
    }
    markDismissed();
    setVisible(false);
  };

  if (!visible) return null;

  const message =
    locale === "ar"
      ? `مرحباً! أريد استخدام كود الخصم ${DISCOUNT_CODE} للحصول على خصم 20%`
      : `Hello! I'd like to use my 20% discount code ${DISCOUNT_CODE}`;
  const whatsappUrl = `https://wa.me/${PHONE_NUMBER}?text=${encodeURIComponent(message)}`;

  return (
    <div
      className="fixed inset-0 z-[9000] flex items-center justify-center p-[16px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="discount-popup-title"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 discount-popup-backdrop"
        style={{ backgroundColor: "rgba(26, 6, 69, 0.7)", backdropFilter: "blur(4px)" }}
        onClick={close}
      />

      {/* Card */}
      <div className="relative w-full max-w-[400px] bg-white rounded-[28px] overflow-hidden shadow-2xl discount-popup-card">
        {/* Decorative header */}
        <div
          className="relative h-[150px] overflow-hidden"
          style={{ background: "linear-gradient(135deg, #1A0645 0%, #2C0F6B 55%, #1A0645 100%)" }}
        >
          {/* Paint splash blobs matching the hero artwork */}
          <span
            className="absolute rounded-full"
            style={{
              top: "-44px",
              insetInlineStart: "-36px",
              width: "150px",
              height: "150px",
              background: "radial-gradient(circle at 30% 30%, #F2E30C, #F2A20C)",
              filter: "blur(1px)",
              opacity: 0.95,
            }}
          />
          <span
            className="absolute rounded-full"
            style={{
              bottom: "-56px",
              insetInlineStart: "30%",
              width: "130px",
              height: "130px",
              background: "radial-gradient(circle at 40% 40%, #52A8F2, #2C6FD1)",
              opacity: 0.9,
            }}
          />
          <span
            className="absolute rounded-full"
            style={{
              top: "-30px",
              insetInlineEnd: "-28px",
              width: "120px",
              height: "120px",
              background: "radial-gradient(circle at 40% 40%, #2BD988, #1FA968)",
              opacity: 0.9,
            }}
          />
          {/* Small confetti dots */}
          <span className="absolute rounded-full" style={{ top: "20px", insetInlineStart: "34%", width: "10px", height: "10px", backgroundColor: "#F2E30C" }} />
          <span className="absolute rounded-full" style={{ bottom: "24px", insetInlineEnd: "18%", width: "8px", height: "8px", backgroundColor: "#52A8F2" }} />
          <span className="absolute rounded-full" style={{ top: "36px", insetInlineEnd: "34%", width: "6px", height: "6px", backgroundColor: "#2BD988" }} />

          {/* 20% badge */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="rotate-[-4deg]"
              style={{
                background: "linear-gradient(180deg, #F2A20C 0%, #F25B22 100%)",
                borderRadius: "9999px",
                padding: "14px 34px",
                boxShadow: "0 10px 26px rgba(0, 0, 0, 0.35)",
                border: "3px solid rgba(255, 255, 255, 0.85)",
              }}
            >
              <span
                className="text-white font-bold whitespace-nowrap"
                style={{ fontSize: "34px", lineHeight: 1, textShadow: "0 2px 5px rgba(0,0,0,0.25)" }}
              >
                {t("badge")}
              </span>
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={close}
            aria-label={t("close")}
            className="absolute flex items-center justify-center rounded-full transition-colors text-white"
            style={{
              top: "12px",
              insetInlineEnd: "12px",
              width: "38px",
              height: "38px",
              fontSize: "18px",
              lineHeight: 1,
              backgroundColor: "rgba(255,255,255,0.22)",
            }}
          >
            &#10005;
          </button>
        </div>

        {/* Body */}
        <div className="text-center" style={{ padding: "22px 24px 24px" }}>
          <h2
            id="discount-popup-title"
            className="font-bold"
            style={{ color: "#1A0645", fontSize: "23px", lineHeight: 1.3 }}
          >
            {t("title")}
          </h2>
          <p style={{ color: "#3D3A55", fontSize: "16px", lineHeight: 1.55, marginTop: "10px" }}>
            {t("description")}
          </p>

          {/* Code box */}
          <button
            onClick={handleCopy}
            className="w-full flex items-center justify-center transition-colors"
            style={{
              marginTop: "18px",
              gap: "12px",
              borderRadius: "16px",
              border: "2px dashed #52A8F2",
              backgroundColor: "#F6F6FF",
              padding: "14px 16px",
            }}
            aria-label={t("copy")}
          >
            <span className="font-bold" style={{ color: "#1A0645", fontSize: "24px", letterSpacing: "0.18em" }}>
              {DISCOUNT_CODE}
            </span>
            <span
              className="font-semibold text-white"
              style={{
                fontSize: "13px",
                borderRadius: "9999px",
                padding: "6px 14px",
                backgroundColor: copied ? "#2BD988" : "#52A8F2",
                transition: "background-color 0.2s",
              }}
            >
              {copied ? t("copied") : t("copy")}
            </span>
          </button>

          {/* WhatsApp CTA */}
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleWhatsAppClick}
            className="w-full flex items-center justify-center text-white font-semibold hover:scale-[1.03] transition-transform"
            style={{
              marginTop: "16px",
              gap: "10px",
              borderRadius: "9999px",
              padding: "16px 24px",
              fontSize: "18px",
              background: "linear-gradient(180deg, #2BD988 0%, #1DA851 100%)",
              boxShadow: "0 8px 20px rgba(37, 211, 102, 0.45)",
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="white" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12.004 2C6.475 2 2 6.475 2 12.004c0 1.939.519 3.817 1.5 5.46L2 22l4.653-1.46a10.01 10.01 0 005.35 1.465c5.528 0 10.004-4.475 10.004-10.004C22.007 6.475 17.532 2 12.004 2zm0 18.26c-1.622 0-3.198-.426-4.582-1.23l-.328-.188-2.761.867.902-2.69-.208-.342a8.01 8.01 0 01-1.25-4.322c0-4.418 3.586-8.004 8.004-8.004s8.004 3.586 8.004 8.004-3.586 8.004-8.004 8.004zm4.37-6.008c-.243-.121-1.437-.707-1.66-.788-.223-.082-.386-.122-.55.121s-.63.788-.772.951c-.141.162-.282.183-.525.061-.243-.122-1.027-.378-1.957-1.203-.723-.646-1.21-1.447-1.35-1.69-.14-.243-.015-.375.106-.497.11-.109.243-.284.364-.426.122-.142.162-.243.243-.405.081-.162.041-.304-.02-.426-.061-.121-.55-1.325-.754-1.811-.198-.475-.4-.411-.55-.42-.142-.008-.304-.01-.466-.01s-.426.061-.65.305c-.223.243-.85.83-.85 2.023 0 1.192.87 2.344.99 2.505.121.162 1.711 2.615 4.145 3.667.58.25 1.032.399 1.385.511.582.186 1.112.16 1.53.097.467-.07 1.437-.586 1.64-1.152.203-.567.203-1.053.142-1.152-.061-.101-.223-.162-.466-.284z" />
            </svg>
            {t("cta")}
          </a>

          {/* Dismiss link */}
          <button
            onClick={close}
            className="underline hover:opacity-70 transition-opacity"
            style={{ marginTop: "14px", fontSize: "14px", color: "#6B6890", textUnderlineOffset: "3px" }}
          >
            {t("dismiss")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WhatsAppDiscountPopup;
